import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import {
  CalendarPlus, Users, Loader2, Check, Search,
  AlertTriangle, ArrowLeft, Sparkles, ShoppingCart, Wallet, ToggleLeft, ToggleRight, EyeOff, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { showPointsCelebration, showLevelUpCelebration } from "@/components/PointsCelebration";
import ChefAvatar from "@/components/ChefAvatar";
import { GustoLogo, GustoIcon } from "@/components/GustoLogo";

const CreateMealPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [friends, setFriends] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [mealDate, setMealDate] = useState("");
  const [mealTime, setMealTime] = useState("");
  const [message, setMessage] = useState("");
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeSearch, setRecipeSearch] = useState("");
  const [showRecipeSearch, setShowRecipeSearch] = useState(false);
  const [publicResults, setPublicResults] = useState([]);
  const [searchingPublic, setSearchingPublic] = useState(false);
  const [isImprovised, setIsImprovised] = useState(false);
  const [extraItems, setExtraItems] = useState([]);
  const [newItemName, setNewItemName] = useState("");
  // Budget / contribution
  const [costPerPerson, setCostPerPerson] = useState("");
  const [contributionEnabled, setContributionEnabled] = useState(false);
  const [contributionPerGuest, setContributionPerGuest] = useState("");
  const [budgetHidden, setBudgetHidden] = useState(false);

  // AI suggestion
  const [suggesting, setSuggesting] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [friendsRes, recipesRes] = await Promise.all([
          axios.get(`${API}/friends`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/my-recipes`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setFriends(friendsRes.data);
        setRecipes(recipesRes.data.filter((r) => r.status === "finished"));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const toggleFriend = (id) => {
    setSelectedFriends((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const addExtraItem = () => {
    if (newItemName.trim()) {
      setExtraItems((prev) => [...prev, { name: newItemName.trim(), quantity: "", unit: "" }]);
      setNewItemName("");
    }
  };

  const askChefGusto = async () => {
    if (selectedFriends.length === 0) {
      toast.error("Sélectionnez au moins un ami");
      return;
    }
    setSuggesting(true);
    try {
      const res = await axios.post(
        `${API}/friends/suggest-recipe`,
        selectedFriends,
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setAiSuggestions(res.data);
    } catch {
      toast.error("Erreur lors de la suggestion");
    } finally {
      setSuggesting(false);
    }
  };

  const createMeal = async () => {
    if (!title.trim() || !mealDate) {
      toast.error("Remplissez le titre et la date");
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/meals`,
        {
          title: title.trim(),
          recipe_id: isImprovised ? null : (selectedRecipe?.id || null),
          recipe_name: isImprovised ? "Recette improvisée" : (selectedRecipe?.name || null),
          is_improvised: isImprovised,
          meal_date: mealDate,
          meal_time: mealTime || null,
          message: message.trim() || null,
          invited_friend_ids: selectedFriends,
          shopping_items: extraItems,
          estimated_budget: costPerPerson && selectedFriends.length > 0
            ? parseFloat(costPerPerson) * (selectedFriends.length + 1) : (costPerPerson ? parseFloat(costPerPerson) : null),
          cost_per_person: costPerPerson ? parseFloat(costPerPerson) : null,
          contribution_enabled: contributionEnabled,
          contribution_per_guest: contributionPerGuest ? parseFloat(contributionPerGuest) : (contributionEnabled && costPerPerson ? parseFloat(costPerPerson) : null),
          budget_hidden: budgetHidden,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Repas créé !");
      navigate(`/meals/${res.data.id}`);
    } catch {
      toast.error("Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const filteredRecipes = recipeSearch
    ? recipes.filter((r) => r.name.toLowerCase().includes(recipeSearch.toLowerCase()))
    : recipes.slice(0, 6);

  // Search public recipes (debounced)
  useEffect(() => {
    if (!recipeSearch || recipeSearch.length < 2) { setPublicResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearchingPublic(true);
      try {
        const res = await axios.get(`${API}/recipes/search?q=${encodeURIComponent(recipeSearch)}`);
        // Exclude own recipes (already in filteredRecipes)
        const ownIds = new Set(recipes.map((r) => r.id));
        setPublicResults(res.data.filter((r) => !ownIds.has(r.id)));
      } catch {} finally { setSearchingPublic(false); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [recipeSearch]);

  // Gather dietary warnings for selected friends
  const getWarnings = () => {
    const warnings = [];
    const selected = friends.filter((f) => selectedFriends.includes(f.id));
    selected.forEach((f) => {
      const dp = f.dietary_profile || {};
      (dp.allergies || []).forEach((a) => {
        warnings.push({ name: f.pseudo, type: "allergy", item: a });
      });
    });
    return warnings;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const warnings = getWarnings();

  return (
    <div className="min-h-screen pb-24 px-4 pt-4" data-testid="create-meal-page">
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl font-bold text-charcoal">Organiser un repas</h1>
            <p className="text-sm text-muted-foreground">Invitez vos amis et partagez les courses</p>
          </div>
        </div>

        {/* Basic Info */}
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Nom du repas</label>
              <Input
                placeholder="Ex: Dîner du samedi soir"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="meal-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-sm font-medium">Date</label>
                <Input
                  type="date"
                  value={mealDate}
                  onChange={(e) => setMealDate(e.target.value)}
                  data-testid="meal-date-input"
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Heure</label>
                <Input
                  type="time"
                  value={mealTime}
                  onChange={(e) => setMealTime(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Message pour les invités</label>
              <Textarea
                placeholder="Un petit mot pour vos convives..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={2}
              />
            </div>
          </CardContent>
        </Card>

        {/* Select Friends */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Inviter des amis
              {selectedFriends.length > 0 && (
                <Badge>{selectedFriends.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            {friends.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {friends.map((f) => {
                  const selected = selectedFriends.includes(f.id);
                  const dp = f.dietary_profile || {};
                  const hasRestrictions = (dp.allergies?.length > 0) || (dp.diet_type && dp.diet_type !== "omnivore");
                  return (
                    <button
                      key={f.id}
                      onClick={() => toggleFriend(f.id)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        selected ? "border-primary bg-primary/5" : "border-gray-200 hover:border-gray-300"
                      }`}
                      data-testid={`select-friend-${f.id}`}
                    >
                      <div className="flex items-center gap-2">
                        <ChefAvatar src={f.avatar} name={f.pseudo} size="sm" showToque={false} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{f.pseudo}</p>
                          {hasRestrictions && (
                            <AlertTriangle className="w-3 h-3 text-amber-500 inline" />
                          )}
                        </div>
                        {selected && <Check className="w-4 h-4 text-primary flex-shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucun ami. Invitez-en depuis la page Amis !
              </p>
            )}
          </CardContent>
        </Card>

        {/* Dietary Warnings */}
        {warnings.length > 0 && (
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <p className="text-sm font-medium text-amber-800 flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4" /> Attention aux restrictions
              </p>
              <div className="space-y-1">
                {warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-700">
                    <strong>{w.name}</strong> est allergique à <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">{w.item}</Badge>
                  </p>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Suggestion */}
        {selectedFriends.length > 0 && (
          <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20">
            <CardContent className="p-4 space-y-3">
              <Button
                onClick={askChefGusto}
                disabled={suggesting}
                variant="outline"
                className="w-full rounded-full gap-2 border-primary/30"
                data-testid="ask-chef-gusto-btn"
              >
                {suggesting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 text-primary" />
                )}
                Chef Gusto, suggère une recette parfaite !
              </Button>
              {aiSuggestions?.recipes && (
                <div className="space-y-2">
                  {aiSuggestions.recipes.map((r, i) => (
                    <motion.button
                      key={i}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.1 }}
                      onClick={() => {
                        setTitle(r.name);
                        toast.success(`"${r.name}" sélectionné !`);
                      }}
                      className="w-full text-left p-3 rounded-xl bg-white border hover:border-primary transition-colors"
                    >
                      <p className="font-medium text-sm text-charcoal">{r.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">{r.description}</p>
                      <p className="text-xs text-green-600 mt-1 italic">{r.why_perfect}</p>
                    </motion.button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Select Recipe */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <GustoIcon className="w-4 h-4" /> Choisir une recette
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            {isImprovised ? (
              <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Recette improvisée
                </p>
                <p className="text-xs text-amber-600 mt-1">
                  Le menu sera adapté aux goûts et allergies des invités
                </p>
                {warnings.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {warnings.map((w, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">
                        {w.name} : {w.item}
                      </Badge>
                    ))}
                  </div>
                )}
                <Button variant="ghost" size="sm" onClick={() => setIsImprovised(false)} className="mt-2 text-xs">
                  Choisir une recette à la place
                </Button>
              </div>
            ) : selectedRecipe ? (
              <div className="flex items-center gap-2 p-2 bg-primary/5 rounded-xl border border-primary/20">
                <GustoIcon className="w-4 h-4" />
                <span className="text-sm font-medium flex-1">{selectedRecipe.name}</span>
                <Button variant="ghost" size="sm" onClick={() => setSelectedRecipe(null)}>
                  <span className="text-xs">Changer</span>
                </Button>
              </div>
            ) : (
              <>
                <Input
                  placeholder="Taper le nom d'une recette..."
                  value={recipeSearch}
                  onChange={(e) => setRecipeSearch(e.target.value)}
                  className="mb-1"
                  data-testid="recipe-search-input"
                />
                <div className="grid grid-cols-1 gap-1 max-h-48 overflow-y-auto">
                  {/* Own recipes */}
                  {filteredRecipes.length > 0 && (
                    <>
                      {recipeSearch && <p className="text-[10px] text-muted-foreground px-1 font-medium">Mes recettes</p>}
                      {filteredRecipes.map((r) => (
                        <button key={r.id} onClick={() => { setSelectedRecipe(r); setRecipeSearch(""); }}
                          className="flex items-center gap-2 p-2 rounded-xl hover:bg-gray-50 text-left transition-colors">
                          <GustoIcon className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm truncate">{r.name}</span>
                          {r.servings && <Badge variant="outline" className="text-[10px] ml-auto flex-shrink-0">{r.servings} pers.</Badge>}
                        </button>
                      ))}
                    </>
                  )}
                  {/* Public recipes */}
                  {publicResults.length > 0 && (
                    <>
                      <p className="text-[10px] text-muted-foreground px-1 font-medium mt-1">Recettes de la communauté</p>
                      {publicResults.map((r) => (
                        <button key={r.id} onClick={() => { setSelectedRecipe(r); setRecipeSearch(""); }}
                          className="flex items-center gap-2 p-2 rounded-xl hover:bg-blue-50 text-left transition-colors">
                          <Users className="w-3 h-3 text-blue-500 flex-shrink-0" />
                          <span className="text-sm truncate">{r.name}</span>
                          {r.cuisine_type && <Badge variant="outline" className="text-[10px] ml-auto flex-shrink-0">{r.cuisine_type}</Badge>}
                        </button>
                      ))}
                    </>
                  )}
                  {searchingPublic && <Loader2 className="w-4 h-4 animate-spin mx-auto my-2 text-muted-foreground" />}
                  {/* No results */}
                  {recipeSearch.length >= 2 && filteredRecipes.length === 0 && publicResults.length === 0 && !searchingPublic && (
                    <p className="text-xs text-muted-foreground text-center py-2">Aucune recette trouvée</p>
                  )}
                </div>
                {/* Improvised recipe button */}
                <Button
                  variant="outline"
                  onClick={() => { setIsImprovised(true); setSelectedRecipe(null); setRecipeSearch(""); }}
                  className="w-full rounded-full gap-2 border-dashed border-amber-300 text-amber-700 hover:bg-amber-50 mt-1"
                  data-testid="improvised-recipe-btn"
                >
                  <Sparkles className="w-4 h-4" />
                  Recette improvisée (adaptée aux invités)
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Extra Shopping Items */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" /> Articles supplémentaires
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-2">
            <p className="text-xs text-muted-foreground">Ajoutez boissons, pain, desserts... en plus de la recette</p>
            <div className="flex gap-2">
              <Input
                placeholder="Ex: 2 baguettes"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addExtraItem()}
              />
              <Button onClick={addExtraItem} variant="outline" size="icon">
                <Check className="w-4 h-4" />
              </Button>
            </div>
            {extraItems.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {extraItems.map((item, i) => (
                  <Badge key={i} variant="outline" className="gap-1">
                    {item.name}
                    <button onClick={() => setExtraItems((p) => p.filter((_, j) => j !== i))}>
                      <span className="text-xs ml-1">x</span>
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Budget & Contribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Wallet className="w-4 h-4" /> Budget & Participation
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Cout par personne</label>
              <div className="relative">
                <Input
                  type="number" min="0" step="0.50" placeholder="Ex: 12.50"
                  value={costPerPerson}
                  onChange={(e) => {
                    setCostPerPerson(e.target.value);
                    if (contributionEnabled && !contributionPerGuest) {
                      setContributionPerGuest(e.target.value);
                    }
                  }}
                  className="pr-10"
                  data-testid="meal-cost-per-person-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">EUR</span>
              </div>
              {costPerPerson && selectedFriends.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Budget total estime : {(parseFloat(costPerPerson) * (selectedFriends.length + 1)).toFixed(2)} EUR ({selectedFriends.length + 1} personnes)
                </p>
              )}
            </div>

            {/* Toggle: demander une participation */}
            <div
              className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 cursor-pointer select-none"
              onClick={() => {
                const next = !contributionEnabled;
                setContributionEnabled(next);
                if (next && costPerPerson && !contributionPerGuest) setContributionPerGuest(costPerPerson);
              }}
              data-testid="contribution-toggle"
            >
              {contributionEnabled
                ? <ToggleRight className="w-8 h-5 text-primary flex-shrink-0" />
                : <ToggleLeft className="w-8 h-5 text-gray-400 flex-shrink-0" />}
              <div className="flex-1">
                <p className="text-sm font-medium text-charcoal">Demander une participation</p>
                <p className="text-xs text-muted-foreground">
                  {contributionEnabled ? "Les invites verront le montant a payer" : "Pas de participation financiere"}
                </p>
              </div>
            </div>
            {contributionEnabled && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="space-y-1">
                <label className="text-sm font-medium">Montant par personne</label>
                <div className="relative">
                  <Input
                    type="number" min="0" step="0.50" placeholder="Ex: 10"
                    value={contributionPerGuest}
                    onChange={(e) => setContributionPerGuest(e.target.value)}
                    className="pr-10"
                    data-testid="contribution-amount-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">EUR</span>
                </div>
                {contributionPerGuest && selectedFriends.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Total attendu : {(parseFloat(contributionPerGuest) * selectedFriends.length).toFixed(2)} EUR ({selectedFriends.length} invite{selectedFriends.length > 1 ? 's' : ''})
                  </p>
                )}
              </motion.div>
            )}

            {/* Toggle: masquer le budget */}
            <div
              className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 cursor-pointer select-none"
              onClick={() => setBudgetHidden(!budgetHidden)}
              data-testid="budget-hidden-toggle"
            >
              {budgetHidden
                ? <EyeOff className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                : <Eye className="w-5 h-5 text-muted-foreground flex-shrink-0" />}
              <div className="flex-1">
                <p className="text-sm font-medium text-charcoal">Masquer le budget aux invites</p>
                <p className="text-xs text-muted-foreground">
                  {budgetHidden ? "Les invites ne verront pas le cout par personne" : "Le budget est visible par tous"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Create Button */}
        <Button
          onClick={createMeal}
          disabled={creating || !title.trim() || !mealDate}
          className="w-full h-14 rounded-full text-lg gap-2"
          data-testid="create-meal-submit-btn"
        >
          {creating ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <CalendarPlus className="w-5 h-5" />
          )}
          Créer le repas et inviter
        </Button>
      </div>
    </div>
  );
};

export default CreateMealPage;
